import {
  createEvent
} from "./chunk-3KMRD34O.mjs";
import {
  CenterSpinner
} from "./chunk-656IBMLL.mjs";
import {
  LockOutline_default
} from "./chunk-VD6QUPVI.mjs";
import {
  Close_default
} from "./chunk-YK3NLIV4.mjs";
import {
  cn
} from "./chunk-DYSUVPH5.mjs";
import {
  __objRest,
  __spreadValues
} from "./chunk-JNHJJ43T.mjs";

// src/input/Input.tsx
import React, { useCallback, useEffect, useRef } from "react";
var inputClassNames = {
  container: "relative w-full space-y-2",
  root: "flex w-full border text-foreground-subtle border-[theme(backgroundColor.interactive-surface)] bg-interactive-surface bg-clip-border appearance-none transition-colors duration-300 ease-out focus-within:bg-surface caret-[theme(backgroundColor.interactive-accent-surface-active)] focus-within:border-[theme(backgroundColor.interactive-accent-surface-active)] focus-within:outline-none my-body-sm rounded",
  input: "block w-full border-0 placeholder-[theme(textColor.foreground-subtle)] bg-clip-border text-inherit appearance-none transition-colors duration-300 ease-out focus:outline-none p-3 my-body-sm rounded bg-transparent",
  clear: "text-foreground-subtle inline-flex flex-none items-center justify-center"
};
var Input = React.forwardRef(
  (_a, ref) => {
    var _b = _a, {
      as,
      startEnhancer,
      endEnhancer,
      autoComplete = "on",
      autoFocus = false,
      disabled = false,
      name = "",
      required = false,
      clearable = false,
      error = false,
      positive = false,
      warning = false,
      isLoading = false,
      type = "text",
      className,
      value,
      onClear,
      readOnly = false
    } = _b, props = __objRest(_b, [
      "as",
      "startEnhancer",
      "endEnhancer",
      "autoComplete",
      "autoFocus",
      "disabled",
      "name",
      "required",
      "clearable",
      "error",
      "positive",
      "warning",
      "isLoading",
      "type",
      "className",
      "value",
      "onClear",
      "readOnly"
    ]);
    const Component = as || "input";
    const inputRef = useRef(null);
    useEffect(() => {
      const timer1 = setTimeout(() => {
        if (inputRef && (inputRef == null ? void 0 : inputRef.current)) {
          if (autoFocus) {
            inputRef.current.focus();
          }
        }
      }, 200);
      return () => {
        clearTimeout(timer1);
      };
    }, [inputRef, ref, autoFocus]);
    const clearValue = useCallback(() => {
      if (inputRef && (inputRef == null ? void 0 : inputRef.current)) {
        const nativeInputValue = Object.getOwnPropertyDescriptor(
          as === "textarea" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype,
          "value"
        );
        if (nativeInputValue) {
          const nativeInputValueSetter = nativeInputValue.set;
          if (nativeInputValueSetter) {
            nativeInputValueSetter.call(inputRef.current, "");
            const event = createEvent("input");
            inputRef.current.dispatchEvent(event);
          }
        }
      }
      if (inputRef && (inputRef == null ? void 0 : inputRef.current)) {
        inputRef.current.focus();
      }
      onClear == null ? void 0 : onClear();
    }, [as, onClear]);
    const isClearable = clearable && !isLoading && !disabled && value !== null && typeof value === "string" && value.length !== 0;
    return /* @__PURE__ */ React.createElement("div", { className: cn(inputClassNames.container, className == null ? void 0 : className.container) }, /* @__PURE__ */ React.createElement(
      "div",
      {
        className: cn(
          inputClassNames.root,
          {
            "text-foreground-disabled": disabled || readOnly,
            "border-semantic-success": positive,
            "border-semantic-critical": error,
            "border-semantic-warning": warning
          },
          className == null ? void 0 : className.root
        ),
        "data-testid": "input-root"
      },
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: cn("flex items-center", {
            "ml-3": startEnhancer
          })
        },
        startEnhancer
      ),
      /* @__PURE__ */ React.createElement(
        Component,
        __spreadValues({
          ref: ref || inputRef,
          className: cn(
            inputClassNames.input,
            {
              "cursor-default": disabled || readOnly
            },
            className == null ? void 0 : className.input
          ),
          autoComplete,
          autoFocus,
          disabled,
          name,
          required,
          type,
          placeholder: props.placeholder,
          value,
          "data-testid": "input",
          readOnly
        }, props)
      ),
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: cn("flex items-center space-x-4 rounded-r", {
            "mr-3": isClearable || isLoading || endEnhancer || disabled
          })
        },
        isClearable && /* @__PURE__ */ React.createElement(
          "button",
          {
            type: "button",
            tabIndex: -1,
            onClick: (e) => {
              e.stopPropagation();
              clearValue();
            },
            "aria-label": "clear selection",
            "data-testid": "input-clear",
            className: cn(
              inputClassNames.clear,
              { "h-12": as === "textarea" },
              className == null ? void 0 : className.clear
            )
          },
          /* @__PURE__ */ React.createElement(Close_default, { width: 18 })
        ),
        isLoading && /* @__PURE__ */ React.createElement(
          "div",
          {
            "aria-label": "loading",
            "data-testid": "input-is-loading",
            className: cn(
              "inline-flex flex-none cursor-wait items-center justify-center",
              {
                "h-12": as === "textarea"
              }
            )
          },
          /* @__PURE__ */ React.createElement(CenterSpinner, { size: 16 })
        ),
        endEnhancer,
        disabled && /* @__PURE__ */ React.createElement(LockOutline_default, { width: 20, height: 20 })
      )
    ));
  }
);
var Input_default = Input;

export {
  inputClassNames,
  Input,
  Input_default
};
